import {
  NUMEROLOGY_CHAPTERS,
  type NumerologyTopic,
} from 'schools/numerology/data/numerologySyllabus';
import { foldSearchText } from 'shared/search/searchUtils';

export type NumerologySearchEntry = {
  id: string;
  tabLabel: string;
  tabLabelHi: string;
  groupLabel: string;
  terms: string;
  matchSnippet?: string;
};

export const NUMEROLOGY_TOPIC_SEARCH_INDEX: NumerologySearchEntry[] = NUMEROLOGY_CHAPTERS.flatMap(
  (chapter) =>
    chapter.topics.map((topic) => ({
      id: topic.id,
      tabLabel: topic.title.en,
      tabLabelHi: topic.title.hi,
      groupLabel: `Module ${chapter.number}: ${chapter.title.en} (${chapter.title.hi})`,
      terms: [
        topic.id,
        topic.title.en,
        topic.title.hi,
        topic.summary.en,
        topic.summary.hi,
        chapter.title.en,
        chapter.title.hi,
        'numerology',
        'ankotish',
        'chaldean',
        'mulank',
        'bhagyank',
        'compound',
      ].join(' '),
    })),
);

export const NUMEROLOGY_SYLLABUS_TOPIC_IDS = NUMEROLOGY_TOPIC_SEARCH_INDEX.map((e) => e.id);

function findMatchSnippet(terms: string, queryWords: string[]): string {
  const termsList = terms.split(/\s+/);
  for (const qw of queryWords) {
    const fq = foldSearchText(qw);
    if (fq.length < 2) continue;
    for (let i = 0; i < termsList.length; i++) {
      if (foldSearchText(termsList[i]).includes(fq)) {
        const start = Math.max(0, i - 1);
        const end = Math.min(termsList.length, i + 3);
        return termsList.slice(start, end).join(' ');
      }
    }
  }
  return '';
}

function syllabusOrderIndex(tabId: string): number {
  const idx = NUMEROLOGY_SYLLABUS_TOPIC_IDS.indexOf(tabId);
  return idx >= 0 ? idx : Number.MAX_SAFE_INTEGER;
}

export function searchNumerologyTopics(query: string): NumerologySearchEntry[] {
  const q = foldSearchText(query).trim();
  if (!q) return [];
  const queryWords = q.split(/\s+/).filter((w) => w.length >= 2);

  const scored = NUMEROLOGY_TOPIC_SEARCH_INDEX.map((entry) => {
    const labelF = foldSearchText(entry.tabLabel);
    const labelHiF = foldSearchText(entry.tabLabelHi);
    const groupF = foldSearchText(entry.groupLabel);
    const termsF = foldSearchText(entry.terms);
    const hay = `${groupF} ${labelF} ${labelHiF} ${termsF}`;
    let rank = 0;

    if (labelF.includes(q) || labelHiF.includes(q)) rank += 300;
    else if (hay.includes(q)) rank += 200;

    for (const w of queryWords) {
      if (labelF.includes(w) || labelHiF.includes(w)) rank += 60;
      else if (termsF.includes(w)) rank += 40;
      else if (groupF.includes(w)) rank += 20;
    }

    if (labelF.startsWith(q) || labelHiF.startsWith(q)) rank += 100;
    else if (groupF.startsWith(q)) rank += 50;

    if (rank <= 0) return null;

    let matchSnippet = '';
    if (!labelF.includes(q) && !labelHiF.includes(q)) {
      matchSnippet = findMatchSnippet(entry.terms, queryWords);
    }

    return { entry: { ...entry, matchSnippet }, rank };
  }).filter(Boolean) as Array<{ entry: NumerologySearchEntry; rank: number }>;

  scored.sort((a, b) => {
    const orderDiff = syllabusOrderIndex(a.entry.id) - syllabusOrderIndex(b.entry.id);
    if (orderDiff !== 0) return orderDiff;
    return b.rank - a.rank;
  });
  return scored.map((x) => x.entry);
}

export function getNumerologyTopicLabel(topicId: string): string {
  const meta = NUMEROLOGY_TOPIC_SEARCH_INDEX.find((e) => e.id === topicId);
  return meta?.tabLabel ?? topicId;
}

export function getNumerologyTopicLabelHi(topicId: string): string {
  const meta = NUMEROLOGY_TOPIC_SEARCH_INDEX.find((e) => e.id === topicId);
  return meta?.tabLabelHi ?? topicId;
}

export function getNumerologyLessonNumber(topicId: string): number | null {
  const idx = NUMEROLOGY_SYLLABUS_TOPIC_IDS.indexOf(topicId);
  return idx >= 0 ? idx + 1 : null;
}

export function formatNumerologyTopicToastTitle(topicId: string, showEnglish: boolean): string {
  const num = getNumerologyLessonNumber(topicId);
  const label = showEnglish ? getNumerologyTopicLabel(topicId) : getNumerologyTopicLabelHi(topicId);
  return num ? `${num}. ${label}` : label;
}

export function getNumerologyTopicIcon(topicId: string): string {
  for (const chapter of NUMEROLOGY_CHAPTERS) {
    const topic = chapter.topics.find((t) => t.id === topicId);
    if (topic) return topic.icon;
  }
  return '🔢';
}

export function getNumerologyTopicReadMinutes(topicId: string): number {
  for (const chapter of NUMEROLOGY_CHAPTERS) {
    const topic = chapter.topics.find((t: NumerologyTopic) => t.id === topicId);
    if (topic) return topic.readMinutes;
  }
  return 7;
}
